/**
 * Wizard state for creating / editing a Calendar Logic Template.
 *
 * Holds the form data + navigation. Persistence is the caller's responsibility:
 * `submit()` no longer writes anywhere — it just freezes the current state into
 * a UI template, which the wizard component then sends to the API mutation hook.
 *
 * Reference data (milestones, strategy codes, default strategy groups) is
 * sourced from the API by the wizard component and passed into the few actions
 * that need to iterate over it.
 */
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type { StepperStep } from '@/components/ui/stepper'
import {
  cellKey,
  cloneBiweeklyOffsetGroups,
  cloneStrategyGroups,
  dynamicForRuleEdit,
  makeBiweeklyGroupId,
  makeBiweeklyTargetId,
  makeEmptyMatrices,
  makeStrategyGroupId,
  suggestBiweeklyLabel,
  suggestGroupLabel,
  type ApplyTarget,
  type BiweeklyOffsetGroup,
  type CltMatrix,
  type Labeled,
  type StrategyGroup,
  type SubstepKey,
  type TargetScope,
  type ValueChooserRule,
} from '@/components/CalendarLogicTemplates/clt.mock'
import type { UiCalendarLogicTemplate } from '@/api/clt.adapter'

export const CLT_WIZARD_STEPS: StepperStep[] = [
  { id: 'details', label: 'Details' },
  { id: 'groups', label: 'Strategy Groups' },
  { id: 'logic', label: 'Milestone Logic' },
  { id: 'schedule', label: 'Biweekly Offset' },
  { id: 'review', label: 'Review' },
]

export const LAST_STEP = CLT_WIZARD_STEPS.length - 1

interface CltWizardState {
  isOpen: boolean
  editingId: string | null
  /** Round-tripped regime field; not currently editable in the UI. */
  regime: 'prior' | 'starting' | undefined
  currentStep: number
  activeSubstep: SubstepKey

  // Step data
  name: string
  description: string
  strategyGroups: StrategyGroup[]
  matrices: Record<SubstepKey, CltMatrix>
  biweeklyOffset: number
  biweeklyOffsetGroups: BiweeklyOffsetGroup[]
  appliesToWeeks: number[]
  applyTargets: ApplyTarget[]
  isDefault: boolean

  // Lifecycle
  openForCreate: (initialGroups: StrategyGroup[]) => void
  openForEdit: (template: UiCalendarLogicTemplate) => void
  close: () => void

  // Navigation
  nextStep: () => void
  prevStep: () => void
  goToStep: (step: number) => void
  setActiveSubstep: (substep: SubstepKey) => void

  // Setters
  setName: (name: string) => void
  setDescription: (description: string) => void

  // Strategy columns (per-template grouping)
  addStrategyGroup: () => void
  removeStrategyGroup: (groupId: string) => void
  setStrategyGroupLabel: (groupId: string, label: string) => void
  /** Set a column's codes; codes are removed from any other column they were in. */
  setStrategyGroupCodes: (groupId: string, codes: string[]) => void

  setCell: (
    substep: SubstepKey,
    milestoneId: string,
    strategyId: string,
    rule: ValueChooserRule | null
  ) => void
  applyRuleToRow: (
    substep: SubstepKey,
    milestoneId: string,
    rule: ValueChooserRule
  ) => void
  /** `milestones` from the caller — needed to iterate the matrix rows. */
  applyRuleToColumn: (
    substep: SubstepKey,
    strategyId: string,
    rule: ValueChooserRule,
    milestones: Labeled[]
  ) => void
  copyColumn: (
    targetSubstep: SubstepKey,
    targetStrategyId: string,
    sourceMatrix: CltMatrix,
    sourceStrategyId: string,
    milestones: Labeled[]
  ) => void
  setBiweeklyOffset: (offset: number) => void
  addBiweeklyOffsetGroup: () => void
  removeBiweeklyOffsetGroup: (id: string) => void
  setBiweeklyOffsetGroupLabel: (id: string, label: string) => void
  setBiweeklyOffsetGroupOffsetDays: (id: string, days: number) => void
  setBiweeklyOffsetGroupMilestoneIds: (
    id: string,
    milestoneIds: string[]
  ) => void
  addBiweeklyTarget: (groupId: string) => void
  removeBiweeklyTarget: (groupId: string, targetId: string) => void
  setBiweeklyTargetStrategyGroup: (
    groupId: string,
    targetId: string,
    strategyGroupId: string
  ) => void
  setBiweeklyTargetIsImport: (
    groupId: string,
    targetId: string,
    isImport: boolean
  ) => void
  setBiweeklyTargetIsRail: (
    groupId: string,
    targetId: string,
    isRail: boolean
  ) => void
  toggleWeek: (week: number) => void
  setWeeks: (weeks: number[]) => void
  addCalendarTarget: (calendarId: string) => void
  removeCalendarTarget: (calendarId: string) => void
  setTargetScope: (calendarId: string, scope: TargetScope) => void
  setTargetEventIds: (calendarId: string, eventIds: string[]) => void
  setIsDefault: (value: boolean) => void

  /** Returns the current draft as a UI template. Caller persists. */
  buildDraft: () => UiCalendarLogicTemplate
}

const blankState = {
  editingId: null as string | null,
  regime: undefined as 'prior' | 'starting' | undefined,
  currentStep: 0,
  activeSubstep: 'IMPORT_RAIL' as SubstepKey,
  name: '',
  description: '',
  strategyGroups: [] as StrategyGroup[],
  matrices: makeEmptyMatrices(),
  biweeklyOffset: 0,
  biweeklyOffsetGroups: [] as BiweeklyOffsetGroup[],
  appliesToWeeks: [] as number[],
  applyTargets: [] as ApplyTarget[],
  isDefault: false,
}

/** Mutate a matrix immutably: set or (when rule is null) delete a cell. */
function withCell(
  matrix: CltMatrix,
  key: string,
  rule: ValueChooserRule | null
): CltMatrix {
  if (rule === null) {
    const next = { ...matrix }
    delete next[key]
    return next
  }
  return { ...matrix, [key]: rule }
}

export const useCltWizardStore = create<CltWizardState>()(
  devtools(
    (set, get) => ({
      isOpen: false,
      ...blankState,

      openForCreate: (initialGroups: StrategyGroup[]) =>
        set({
          isOpen: true,
          ...blankState,
          strategyGroups: cloneStrategyGroups(initialGroups),
        }),

      openForEdit: (template: UiCalendarLogicTemplate) =>
        set({
          isOpen: true,
          editingId: template.id,
          regime: template._regime,
          currentStep: 0,
          activeSubstep: 'IMPORT_RAIL',
          name: template.name,
          description: template.description ?? '',
          strategyGroups: cloneStrategyGroups(template.strategyGroups ?? []),
          matrices: {
            IMPORT_RAIL: { ...template.matrices.IMPORT_RAIL },
            IMPORT_NO_RAIL: { ...template.matrices.IMPORT_NO_RAIL },
            DOMESTIC_RAIL: { ...template.matrices.DOMESTIC_RAIL },
            DOMESTIC_NO_RAIL: { ...template.matrices.DOMESTIC_NO_RAIL },
          },
          biweeklyOffset: template.biweeklyOffset,
          biweeklyOffsetGroups: cloneBiweeklyOffsetGroups(
            template.biweeklyOffsetGroups ?? []
          ),
          appliesToWeeks: [...template.appliesToWeeks],
          applyTargets: template.applyTargets.map(t => ({
            ...t,
            eventIds: [...t.eventIds],
          })),
          isDefault: template.isDefault,
        }),

      close: () => set({ isOpen: false }),

      nextStep: () =>
        set(state => ({
          currentStep: Math.min(LAST_STEP, state.currentStep + 1),
        })),
      prevStep: () =>
        set(state => ({ currentStep: Math.max(0, state.currentStep - 1) })),
      goToStep: (step: number) => set({ currentStep: step }),
      setActiveSubstep: (substep: SubstepKey) =>
        set({ activeSubstep: substep }),

      setName: (name: string) => set({ name }),
      setDescription: (description: string) => set({ description }),

      addStrategyGroup: () =>
        set(state => ({
          strategyGroups: [
            ...state.strategyGroups,
            {
              id: makeStrategyGroupId(),
              label: suggestGroupLabel([]),
              codes: [],
            },
          ],
        })),

      removeStrategyGroup: (groupId: string) =>
        set(state => {
          // Drop the column AND strip its cells from every matrix.
          const matrices = Object.fromEntries(
            Object.entries(state.matrices).map(([substep, matrix]) => [
              substep,
              Object.fromEntries(
                Object.entries(matrix).filter(
                  ([key]) => key.split('__')[1] !== groupId
                )
              ),
            ])
          ) as Record<SubstepKey, CltMatrix>
          const biweeklyOffsetGroups = state.biweeklyOffsetGroups.map(bg => {
            const next = bg.targets.filter(t => t.strategyGroupId !== groupId)
            return next.length === bg.targets.length
              ? bg
              : { ...bg, targets: next }
          })
          return {
            strategyGroups: state.strategyGroups.filter(g => g.id !== groupId),
            matrices,
            biweeklyOffsetGroups,
          }
        }),

      setStrategyGroupLabel: (groupId: string, label: string) =>
        set(state => ({
          strategyGroups: state.strategyGroups.map(g =>
            g.id === groupId ? { ...g, label } : g
          ),
        })),

      setStrategyGroupCodes: (groupId: string, codes: string[]) =>
        set(state => ({
          // A code belongs to exactly one column: remove it from the others.
          strategyGroups: state.strategyGroups.map(g => {
            if (g.id === groupId) return { ...g, codes: [...codes] }
            const filtered = g.codes.filter(c => !codes.includes(c))
            return filtered.length === g.codes.length
              ? g
              : { ...g, codes: filtered }
          }),
        })),

      setCell: (substep, milestoneId, strategyId, rule) =>
        set(state => ({
          matrices: {
            ...state.matrices,
            [substep]: withCell(
              state.matrices[substep],
              cellKey(milestoneId, strategyId),
              rule
            ),
          },
        })),

      applyRuleToRow: (substep, milestoneId, rule) =>
        set(state => {
          let next = state.matrices[substep]
          state.strategyGroups.forEach(group => {
            const key = cellKey(milestoneId, group.id)
            next = withCell(next, key, {
              ...rule,
              dynamic: dynamicForRuleEdit(rule.mode, next[key]),
            })
          })
          return { matrices: { ...state.matrices, [substep]: next } }
        }),

      applyRuleToColumn: (substep, strategyId, rule, milestones) =>
        set(state => {
          let next = state.matrices[substep]
          milestones.forEach(milestone => {
            // Skip self-reference: never point a milestone's cell at itself.
            if (rule.fromMilestoneId === milestone.id) return
            const key = cellKey(milestone.id, strategyId)
            next = withCell(next, key, {
              ...rule,
              dynamic: dynamicForRuleEdit(rule.mode, next[key]),
            })
          })
          return { matrices: { ...state.matrices, [substep]: next } }
        }),

      copyColumn: (
        targetSubstep,
        targetStrategyId,
        sourceMatrix,
        sourceStrategyId,
        milestones
      ) =>
        set(state => {
          let next = state.matrices[targetSubstep]
          milestones.forEach(milestone => {
            const srcRule =
              sourceMatrix[cellKey(milestone.id, sourceStrategyId)]
            next = withCell(
              next,
              cellKey(milestone.id, targetStrategyId),
              srcRule ? { ...srcRule } : null
            )
          })
          return { matrices: { ...state.matrices, [targetSubstep]: next } }
        }),

      setBiweeklyOffset: (offset: number) => set({ biweeklyOffset: offset }),

      addBiweeklyOffsetGroup: () =>
        set(state => ({
          biweeklyOffsetGroups: [
            ...state.biweeklyOffsetGroups,
            {
              id: makeBiweeklyGroupId(),
              label: suggestBiweeklyLabel(state.biweeklyOffsetGroups.length),
              offsetDays: -7,
              milestoneIds: [],
              targets: [],
            },
          ],
        })),

      removeBiweeklyOffsetGroup: (id: string) =>
        set(state => ({
          biweeklyOffsetGroups: state.biweeklyOffsetGroups.filter(
            g => g.id !== id
          ),
        })),

      setBiweeklyOffsetGroupLabel: (id: string, label: string) =>
        set(state => ({
          biweeklyOffsetGroups: state.biweeklyOffsetGroups.map(g =>
            g.id === id ? { ...g, label } : g
          ),
        })),

      setBiweeklyOffsetGroupOffsetDays: (id: string, offsetDays: number) =>
        set(state => ({
          biweeklyOffsetGroups: state.biweeklyOffsetGroups.map(g =>
            g.id === id ? { ...g, offsetDays } : g
          ),
        })),

      setBiweeklyOffsetGroupMilestoneIds: (
        id: string,
        milestoneIds: string[]
      ) =>
        set(state => ({
          biweeklyOffsetGroups: state.biweeklyOffsetGroups.map(g =>
            g.id === id ? { ...g, milestoneIds: [...milestoneIds] } : g
          ),
        })),

      addBiweeklyTarget: (groupId: string) =>
        set(state => ({
          biweeklyOffsetGroups: state.biweeklyOffsetGroups.map(g =>
            g.id === groupId
              ? {
                  ...g,
                  targets: [
                    ...g.targets,
                    {
                      id: makeBiweeklyTargetId(),
                      strategyGroupId: '',
                      isImport: false,
                      isRail: false,
                    },
                  ],
                }
              : g
          ),
        })),

      removeBiweeklyTarget: (groupId: string, targetId: string) =>
        set(state => ({
          biweeklyOffsetGroups: state.biweeklyOffsetGroups.map(g =>
            g.id === groupId
              ? { ...g, targets: g.targets.filter(t => t.id !== targetId) }
              : g
          ),
        })),

      setBiweeklyTargetStrategyGroup: (
        groupId: string,
        targetId: string,
        strategyGroupId: string
      ) =>
        set(state => ({
          biweeklyOffsetGroups: state.biweeklyOffsetGroups.map(g =>
            g.id === groupId
              ? {
                  ...g,
                  targets: g.targets.map(t =>
                    t.id === targetId ? { ...t, strategyGroupId } : t
                  ),
                }
              : g
          ),
        })),

      setBiweeklyTargetIsImport: (
        groupId: string,
        targetId: string,
        isImport: boolean
      ) =>
        set(state => ({
          biweeklyOffsetGroups: state.biweeklyOffsetGroups.map(g =>
            g.id === groupId
              ? {
                  ...g,
                  targets: g.targets.map(t =>
                    t.id === targetId ? { ...t, isImport } : t
                  ),
                }
              : g
          ),
        })),

      setBiweeklyTargetIsRail: (
        groupId: string,
        targetId: string,
        isRail: boolean
      ) =>
        set(state => ({
          biweeklyOffsetGroups: state.biweeklyOffsetGroups.map(g =>
            g.id === groupId
              ? {
                  ...g,
                  targets: g.targets.map(t =>
                    t.id === targetId ? { ...t, isRail } : t
                  ),
                }
              : g
          ),
        })),

      toggleWeek: (week: number) =>
        set(state => ({
          appliesToWeeks: state.appliesToWeeks.includes(week)
            ? state.appliesToWeeks.filter(w => w !== week)
            : [...state.appliesToWeeks, week].sort((a, b) => a - b),
        })),
      setWeeks: (weeks: number[]) =>
        set({ appliesToWeeks: [...weeks].sort((a, b) => a - b) }),

      addCalendarTarget: (calendarId: string) =>
        set(state =>
          state.applyTargets.some(t => t.calendarId === calendarId)
            ? state
            : {
                applyTargets: [
                  ...state.applyTargets,
                  { calendarId, scope: 'ALL', eventIds: [] },
                ],
              }
        ),
      removeCalendarTarget: (calendarId: string) =>
        set(state => ({
          applyTargets: state.applyTargets.filter(
            t => t.calendarId !== calendarId
          ),
        })),
      setTargetScope: (calendarId: string, scope: TargetScope) =>
        set(state => ({
          applyTargets: state.applyTargets.map(t =>
            t.calendarId === calendarId ? { ...t, scope } : t
          ),
        })),
      setTargetEventIds: (calendarId: string, eventIds: string[]) =>
        set(state => ({
          applyTargets: state.applyTargets.map(t =>
            t.calendarId === calendarId ? { ...t, eventIds } : t
          ),
        })),
      setIsDefault: (value: boolean) => set({ isDefault: value }),

      buildDraft: () => {
        const state = get()
        const draft: UiCalendarLogicTemplate = {
          id: state.editingId ?? `clt-${crypto.randomUUID().slice(0, 8)}`,
          name: state.name.trim(),
          description: state.description.trim() || undefined,
          strategyGroups: state.strategyGroups,
          matrices: state.matrices,
          biweeklyOffset: state.biweeklyOffset,
          biweeklyOffsetGroups: cloneBiweeklyOffsetGroups(
            state.biweeklyOffsetGroups
          ),
          appliesToWeeks: state.appliesToWeeks,
          applyTargets: state.applyTargets.map(t => ({
            calendarId: t.calendarId,
            scope: t.scope,
            eventIds: t.scope === 'SPECIFIC' ? t.eventIds : [],
          })),
          isDefault: state.isDefault,
          updatedAt: new Date().toISOString(),
          _regime: state.regime,
        }
        return draft
      },
    }),
    { name: 'clt-wizard-store' }
  )
)

/** Map the API reference shape to the wizard's local `StrategyGroup` shape. */
export function defaultGroupsToStrategyGroups(
  apiGroups: { id: string; label: string; codes: string[] }[]
): StrategyGroup[] {
  return apiGroups.map(g => ({
    id: g.id,
    label: g.label,
    codes: g.codes,
  }))
}
